import { useState, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LineChart, ShieldCheck, LogOut, MessageSquare, CalendarRange, User as UserIcon, Sparkles, LifeBuoy, RotateCcw } from "lucide-react";
import { KaayaWordmark } from "@/components/KaayaLogo";
import SupportFeedbackDialog from "@/components/SupportFeedbackDialog";
import OfflineBanner from "@/components/OfflineBanner";
import { RefreshContext } from "@/lib/refresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

const TABS = [
  { to: "/chat", icon: MessageSquare, label: "Chat", testid: "tab-chat" },
  { to: "/tracker", icon: LineChart, label: "Tracker", testid: "tab-tracker" },
  { to: "/planner", icon: CalendarRange, label: "Planner", testid: "tab-planner" },
  { action: "support", icon: LifeBuoy, label: "Support", testid: "tab-support" },
];

function tierState(user) {
  const p = user?.profile || {};
  const plan = p.tier_plan;
  const isLifetime = plan === "lifetime";
  let active = false;
  if (isLifetime) active = true;
  else if (p.tier_expires_at) active = new Date(p.tier_expires_at).getTime() > Date.now();
  return { active, isTrial: plan === "trial" };
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [supportOpen, setSupportOpen] = useState(false);

  // Pull-to-refresh: pages register a loader via RefreshContext; the gesture lives on <main>.
  const mainRef = useRef(null);
  const refreshFn = useRef(null);
  const [hasRefresh, setHasRefresh] = useState(false);
  const registerRefresh = useCallback((fn) => { refreshFn.current = fn; setHasRefresh(!!fn); }, []);
  const doRefresh = useCallback(async () => { if (refreshFn.current) await refreshFn.current(); }, []);
  const { pull, refreshing } = usePullToRefresh(mainRef, doRefresh, hasRefresh);

  const NavLink = ({ to, icon: Icon, label, testid }) => {
    const active = loc.pathname === to;
    return (
      <button
        data-testid={testid}
        onClick={() => nav(to)}
        className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full text-sm transition-all duration-300
        ${active ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground hover:bg-muted"}`}
      >
        <Icon size={16} strokeWidth={1.75} />
        <span className="hidden lg:inline">{label}</span>
      </button>
    );
  };

  const TierBadge = ({ className = "" }) => {
    const { active, isTrial } = tierState(user);
    if (active && isTrial) {
      return (
        <button onClick={() => nav("/billing")} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-foreground/70 border border-border ${className}`} data-testid="trial-badge">
          <Sparkles size={11} /> TRIAL
        </button>
      );
    }
    if (active) {
      return (
        <button onClick={() => nav("/billing")} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-secondary/15 text-secondary ${className}`} data-testid="pro-badge">
          <Sparkles size={11} /> PRO
        </button>
      );
    }
    return (
      <button onClick={() => nav("/billing")} className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 ${className}`} data-testid="upgrade-cta">
        <Sparkles size={11} /> Upgrade
      </button>
    );
  };

  return (
    <div className="flex flex-col h-[100dvh] md:h-auto md:min-h-screen">
      {/* ---- Desktop header (hidden on mobile) ---- */}
      <header className="hidden md:block sticky top-0 z-40 glass-panel border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/chat" className="flex items-center gap-2" data-testid="nav-logo">
            <KaayaWordmark size="sm" />
          </Link>
          <nav className="flex items-center gap-1">
            <NavLink to="/chat" icon={MessageSquare} label="Chat" testid="nav-chat" />
            <NavLink to="/tracker" icon={LineChart} label="Tracker" testid="nav-tracker" />
            <NavLink to="/planner" icon={CalendarRange} label="Planner" testid="nav-planner" />
            <button
              data-testid="nav-support"
              onClick={() => setSupportOpen(true)}
              className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-all duration-300"
            >
              <LifeBuoy size={16} strokeWidth={1.75} />
              <span className="hidden lg:inline">Support</span>
            </button>
            {user?.is_admin && <NavLink to="/admin" icon={ShieldCheck} label="Admin" testid="nav-admin" />}
          </nav>
          <div className="flex items-center gap-3">
            <TierBadge />
            <button
              onClick={() => nav("/profile")}
              data-testid="nav-profile"
              className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 transition-colors hover:bg-muted ${loc.pathname === "/profile" ? "ring-2 ring-secondary" : ""}`}
            >
              {user?.picture
                ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                : <span className="w-8 h-8 grid place-items-center rounded-full bg-muted text-foreground/70"><UserIcon size={18} /></span>}
              <span className="text-sm text-foreground/80" data-testid="nav-user-name">{user?.name}</span>
            </button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout">
              <LogOut size={16} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* ---- Mobile header (minimal, hidden on desktop) ---- */}
      <header className="md:hidden shrink-0 z-40 glass-panel border-b border-border pt-[env(safe-area-inset-top)]">
        <div className="h-14 px-4 flex items-center justify-between">
          <Link to="/chat" className="flex items-center gap-2" data-testid="nav-logo-mobile">
            <KaayaWordmark size="sm" />
          </Link>
          <div className="flex items-center gap-2">
            <TierBadge />
            {user?.is_admin && (
              <button onClick={() => nav("/admin")} className="p-1.5 rounded-full hover:bg-muted text-foreground/70" data-testid="nav-admin-mobile" aria-label="Admin">
                <ShieldCheck size={18} strokeWidth={1.75} />
              </button>
            )}
            <button
              onClick={() => nav("/profile")}
              data-testid="nav-profile-mobile"
              aria-label="Profile"
              className={`rounded-full transition-transform active:scale-95 ${loc.pathname === "/profile" ? "ring-2 ring-secondary" : ""}`}
            >
              {user?.picture
                ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                : <span className="w-8 h-8 grid place-items-center rounded-full bg-muted text-foreground/70"><UserIcon size={18} /></span>}
            </button>
          </div>
        </div>
      </header>

      <TrialBanner user={user} onUpgrade={() => nav("/billing")} />
      <OfflineBanner />

      <main ref={mainRef} className="relative flex-1 min-h-0 overflow-y-auto md:overflow-visible">
        {(pull > 0 || refreshing) && (
          <div
            className="md:hidden absolute left-0 right-0 top-0 z-30 flex justify-center pointer-events-none"
            style={{ transform: `translateY(${Math.max(pull - 20, 0)}px)`, opacity: Math.min(pull / 70, 1) }}
            data-testid="pull-to-refresh-indicator"
          >
            <span className="mt-1 grid place-items-center h-9 w-9 rounded-full bg-surface-elevated border border-border shadow-sm text-secondary">
              <RotateCcw size={17} className={refreshing ? "animate-spin" : ""} style={{ transform: refreshing ? undefined : `rotate(${pull * 3}deg)` }} />
            </span>
          </div>
        )}
        <RefreshContext.Provider value={registerRefresh}>{children}</RefreshContext.Provider>
      </main>

      {/* ---- Mobile bottom tab bar (hidden on desktop) ---- */}
      <nav
        className="md:hidden shrink-0 z-40 bg-surface-elevated/95 backdrop-blur-xl border-t border-border pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-tab-bar"
      >
        <div className="flex items-stretch justify-around">
          {TABS.map(({ to, action, icon: Icon, label, testid }) => {
            const active = action ? false : loc.pathname === to;
            return (
              <button
                key={to || action}
                onClick={() => (action === "support" ? setSupportOpen(true) : nav(to))}
                data-testid={testid}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-transform active:scale-95
                ${active ? "text-secondary" : "text-foreground/55"}`}
              >
                <Icon size={22} strokeWidth={active ? 2.25 : 1.75} />
                <span className={`text-[10px] tracking-wide ${active ? "font-semibold" : "font-medium"}`}>{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <SupportFeedbackDialog open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}

function TrialBanner({ user, onUpgrade }) {
  const p = user?.profile || {};
  if (p.tier_plan !== "trial" || !p.tier_expires_at) return null;
  const msLeft = new Date(p.tier_expires_at).getTime() - Date.now();
  if (msLeft <= 0) return null;
  const hours = Math.floor(msLeft / 3600000);
  const mins = Math.floor((msLeft % 3600000) / 60000);
  const left =
    hours >= 1 ? `${hours} hour${hours > 1 ? "s" : ""}` :
    mins >= 1 ? `${mins} minute${mins > 1 ? "s" : ""}` :
    "less than a minute";
  return (
    <div className="shrink-0 bg-secondary/10 border-b border-secondary/30" data-testid="trial-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground/85 min-w-0">
          <Sparkles size={14} className="text-secondary shrink-0" />
          <span className="truncate">
            <strong>Your free trial ends in {left}.</strong>
            <span className="hidden sm:inline"> Keep your data, plans &amp; coaching going — pick a plan.</span>
          </span>
        </div>
        <Button size="sm" onClick={onUpgrade} className="rounded-full bg-secondary hover:bg-secondary/90 shrink-0" data-testid="trial-banner-upgrade">
          Upgrade
        </Button>
      </div>
    </div>
  );
}
